import { Box, Button, SxProps } from "@outschool/backpack";
import { useTranslation } from "@outschool/localization";
import truncate from "lodash/truncate";
import React from "react";

type CollapsedContentProps = {
  maxCharacters: number;
  children: string;
  component?: React.ElementType;
  sx?: SxProps;
  onToggle?: (collapsed: boolean, fn: () => void) => void;
  buttonRef?: React.Ref<any>;
  allowCollapse?: boolean;
  showMoreLabel?: string;
  showLessLabel?: string;
  buttonSx?: SxProps;
  additionalContent?: React.ReactElement | null;
};

export default function CollapsedContent({
  maxCharacters,
  children,
  component = "pre",
  sx,
  onToggle,
  buttonRef,
  allowCollapse = true,
  showMoreLabel,
  showLessLabel,
  buttonSx = {},
  additionalContent,
}: CollapsedContentProps) {
  const { t } = useTranslation(
    "ui-components-website\\reviews\\CollapsedContent"
  );
  const showCollapse =
    (children && children.length > maxCharacters) || Boolean(additionalContent);
  const [collapsed, setCollapsed] = React.useState(showCollapse);

  return (
    <Box sx={sx} component={component}>
      {collapsed
        ? truncate(children, {
            length: maxCharacters,
            separator: " ",
          }).trimRight()
        : children}
      {collapsed ? null : additionalContent}
      {showCollapse && (collapsed || allowCollapse) && (
        <React.Fragment>
          &nbsp;
          <Button
            variant="link"
            sx={buttonSx}
            ref={buttonRef}
            onClick={() => {
              if (onToggle) {
                onToggle(!collapsed, () => {
                  setCollapsed(!collapsed);
                });
              } else {
                setCollapsed(!collapsed);
              }
            }}
          >
            {collapsed
              ? showMoreLabel ?? t("Show More...")
              : showLessLabel ?? t("Show Less...")}
          </Button>
        </React.Fragment>
      )}
    </Box>
  );
}
